/**
 * bureau bravo: Öffnet das EU-Gewährleistungslabel im Modal (snippets/bb-gewaehrleistungslabel.liquid).
 * Sleek schließt offene Drawer/Modals per keyup Escape auf document (ModalComponent in theme.js). Der native Dialog schließt schon beim keydown und gibt
 * den Fokus an „Label ansehen“ im Drawer zurück, der folgende keyup würde also den Drawer mitschließen.
 * Deshalb fangen wir das Escape (keydown und den zugehörigen keyup) ab, solange bzw. nachdem das Label offen war.
 */
class BbGewaehrleistungslabel extends HTMLElement {
  #dialog = null;
  #lockedByUs = false;
  #swallowEscapeKeyup = false;

  connectedCallback() {
    const button = this.querySelector('.bb-gewaehrleistung__open');
    button?.addEventListener('click', this.#open);
    // Dialog (und damit das Label-Bild) schon vor dem Klick anlegen, damit es beim Öffnen geladen ist
    button?.addEventListener('pointerenter', this.#prepare, { once: true });
    button?.addEventListener('focus', this.#prepare, { once: true });
  }

  disconnectedCallback() {
    this.querySelector('.bb-gewaehrleistung__open')?.removeEventListener('click', this.#open);
    this.#removeKeyListeners();
  }

  #prepare = () => this.#getDialog();

  #open = () => {
    const dialog = this.#getDialog();
    if (!dialog || dialog.open) return;

    // Scroll-Sperre nur, wenn nicht schon der Drawer sperrt (Sleek: body.modal-show)
    this.#lockedByUs = !document.body.classList.contains('modal-show');
    if (this.#lockedByUs) document.documentElement.style.overflow = 'hidden';

    window.addEventListener('keydown', this.#onKeydown, true);
    window.addEventListener('keyup', this.#onKeyup, true);
    dialog.showModal();
  };

  #onKeydown = (event) => {
    if (!this.#dialog?.open || event.key !== 'Escape') return;

    event.preventDefault();
    event.stopPropagation();
    this.#swallowEscapeKeyup = true;
    this.#dialog.close();
  };

  #onKeyup = (event) => {
    if (event.key !== 'Escape' || !this.#swallowEscapeKeyup) return;

    event.stopPropagation();
    this.#swallowEscapeKeyup = false;
    this.#removeKeyListeners();
  };

  #removeKeyListeners() {
    window.removeEventListener('keydown', this.#onKeydown, true);
    window.removeEventListener('keyup', this.#onKeyup, true);
  }

  #getDialog() {
    if (this.#dialog) return this.#dialog;

    const template = this.querySelector('template');
    const dialog = template?.content.firstElementChild?.cloneNode(true);
    if (!(dialog instanceof HTMLDialogElement)) return null;

    dialog.addEventListener('click', (event) => {
      // Klick auf den Hintergrund trifft den Dialog selbst (padding: 0)
      if (event.target === dialog || event.target.closest('[data-bb-gewaehrleistung-close]')) dialog.close();
    });
    dialog.addEventListener('close', () => {
      // Nach Escape bleibt der keyup-Filter bis zum keyup aktiv, sonst sofort aufräumen
      if (!this.#swallowEscapeKeyup) this.#removeKeyListeners();
      if (this.#lockedByUs) document.documentElement.style.overflow = '';
      this.#lockedByUs = false;
    });

    this.append(dialog);
    this.#dialog = dialog;
    return dialog;
  }
}

if (!customElements.get('bb-gewaehrleistungslabel')) {
  customElements.define('bb-gewaehrleistungslabel', BbGewaehrleistungslabel);
}
